// WHAT RIKSU WILL ACCEPT, asked before anybody holds a credential.
//
// `session.ts` answers *who is signed in*. This answers the other half — *can a sign-in
// succeed here at all* — and the two are different questions with different failure
// directions: a person who is not signed in is ordinary, and a deployment that cannot
// accept a token is broken.
//
// # It reads the one document written for this moment
//
// `GET /.well-known/oauth-protected-resource` is RFC 9728 protected-resource metadata.
// Riksu serves it `security: []` precisely because it is "the one document a caller reads
// before it has a credential", and its own contract says what it may therefore contain:
// the authorization server and the scopes, and nothing else. No tenant, no version, no
// internal host. So rendering exactly those members on a signed-out page discloses
// nothing that address does not already disclose to anybody who asks for it.
//
// It was declared HEADLESS in `tools/check-parity.mjs` with the reason "there is no
// person in that moment". That was true while riksu had no sign-in screen of its own.
// There is a person in that moment now, standing in front of the door, and the entry is
// removed rather than kept as an exemption nothing satisfies.
//
// # A 503 here is the whole point of the endpoint
//
// `riksu_api::discovery` answers **503** when no verifier is configured, and says why:
// naming an authorization server it cannot actually verify against "would send a client
// through a whole authorization flow to arrive at a 503 — later, and with a browser
// window open". A sign-in screen is a client in exactly that position, so it asks first.
//
// # And anything else leaves the door alone
//
// `unknown` is the fail-OPEN answer and it is deliberate. A probe that failed for a
// reason of its own — an offline browser, a proxy, a deployment that does not route the
// well-known path — must never be the thing that hides the only way into the product.
// Only riksu's own definite refusal closes the door.

import { ApiError, call, type components } from "../lib/api";

/** RFC 9728 protected-resource metadata, as riksu's contract declares it. */
export type Metadata = components["schemas"]["ProtectedResourceMetadata"];

/** Where the metadata lives. `riksu_api::discovery::METADATA_PATH`. */
export const METADATA_PATH = "/.well-known/oauth-protected-resource";

export type WayIn =
  /** Not answered yet. The screen says so rather than spinning. */
  | { readonly status: "asking" }
  /** Riksu named an authorization server it accepts tokens from. */
  | { readonly status: "open"; readonly metadata: Metadata }
  /**
   * Riksu answered 503, which on this document means it can name no authorization
   * server at all. A sign-in started here would come back refused.
   */
  | { readonly status: "closed" }
  /** Riksu did not answer the question. Says so; changes nothing. */
  | { readonly status: "unknown" };

/** Asks riksu what it will accept. Never throws. */
export async function askTheWayIn(signal?: AbortSignal): Promise<WayIn> {
  try {
    return { status: "open", metadata: await call<Metadata>(METADATA_PATH, { signal }) };
  } catch (failure) {
    if (failure instanceof ApiError && failure.status === 503) {
      return { status: "closed" };
    }
    return { status: "unknown" };
  }
}
